import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCode, faPenToSquare, faSlidersH } from '@fortawesome/pro-duotone-svg-icons'

import { OrderStatus } from '../../../enums'

import GenericModal from '../../../components/component-items/modal'
import { FulfillmentItemUpdate, SplitOrdersGroup, SplitOrderItem, SplitOrderItemDisposition } from './types'
import {
  SplitOrdersCardContainer,
  SplitOrdersHeader,
  SplitOrdersMeta,
  SplitOrdersMetaSeparator,
  SplitOrderEntry,
  SplitOrderEntryHeader,
  SplitOrderIdentity,
  SplitOrderBadge,
  SplitOrderItemsTable,
  SplitOrderEmpty,
  SplitOrdersActions,
  SplitOrdersActionsButton,
  SplitOrdersActionsMenu,
} from './split-orders.styles'
import { SplitOrdersEditCanonicalModal } from './split-orders-edit-modal'

type SplitOrdersCardProps = {
  group: SplitOrdersGroup
  currentOrderId: string
  onUpdateFulfillmentItems?: (items: FulfillmentItemUpdate[]) => Promise<void>
}

const dispositionCopy: Record<SplitOrderItemDisposition, string> = {
  SHIP: 'Ships',
  BACKORDER: 'Backorder',
  CANCEL: 'Cancelled',
}

const ORDER_STATUS_LABELS: Record<number, string> = {
  [OrderStatus.Cancelled]: 'Cancelled',
  [OrderStatus.Project]: 'Project',
  [OrderStatus.Paused]: 'Paused',
  [OrderStatus.Backordered]: 'Backordered',
  [OrderStatus.Received]: 'Received',
  [OrderStatus.Processing]: 'Processing',
  [OrderStatus.Picked]: 'Picked',
  [OrderStatus.Packed]: 'Packed',
  [OrderStatus.LabelPurchaseFail]: 'Label Purchase Failed',
  [OrderStatus.RateLimitExceeded]: 'Rate Limit Exceeded',
  [OrderStatus.LabelPurchased]: 'Label Purchased',
  [OrderStatus.Shipped]: 'Shipped',
  [OrderStatus.Delivered]: 'Delivered',
}

type SplitOrderStatusTone = 'default' | 'warning' | 'danger' | 'success'

const formatDisposition = (disposition?: SplitOrderItemDisposition) => {
  if (!disposition) {
    return null
  }

  return dispositionCopy[disposition] || disposition
}

const resolveStatusTone = (statusId?: number | string | null): SplitOrderStatusTone => {
  if (statusId === undefined || statusId === null) {
    return 'default'
  }

  const numericStatus = Number(statusId)

  if (Number.isNaN(numericStatus)) {
    return 'default'
  }

  if (
    [OrderStatus.Project, OrderStatus.Paused, OrderStatus.Backordered, OrderStatus.RateLimitExceeded, 155].includes(
      numericStatus
    )
  ) {
    return 'warning'
  }

  if ([OrderStatus.Cancelled, OrderStatus.LabelPurchaseFail].includes(numericStatus)) {
    return 'danger'
  }

  if ([OrderStatus.Shipped, OrderStatus.Delivered].includes(numericStatus)) {
    return 'success'
  }

  return 'default'
}

const resolveStatusLabel = (statusLabel?: string | null, statusId?: number | string | null): string | null => {
  if (statusLabel) {
    return statusLabel
  }

  if (statusId === undefined || statusId === null) {
    return null
  }

  const numericStatus = Number(statusId)

  if (Number.isNaN(numericStatus)) {
    return null
  }

  if (ORDER_STATUS_LABELS[numericStatus]) {
    return ORDER_STATUS_LABELS[numericStatus]
  }

  const statusKey = (OrderStatus as unknown as Record<number, string>)[numericStatus]

  if (typeof statusKey === 'string') {
    return statusKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .trim()
      .replace(/^./, (char) => char.toUpperCase())
  }

  return null
}

const formatRole = (role?: string) => {
  if (!role) {
    return null
  }

  if (role === 'CANONICAL') {
    return 'Canonical'
  }

  if (role === 'FULFILLMENT') {
    return 'Fulfillment'
  }

  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
}

const renderItemQuantity = (item: SplitOrderItem) => {
  const quantities = [item.allocatedQuantity, item.quantity, item.requestedQuantity]
  const value = quantities.find((quantity) => typeof quantity === 'number')

  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : value.toFixed(2)
  }

  return '-'
}

export const SplitOrdersCard = ({ group, currentOrderId, onUpdateFulfillmentItems }: SplitOrdersCardProps) => {
  if (!group?.orders?.length) {
    return null
  }

  const hasMultipleOrders = group.orders.length > 1

  if (!hasMultipleOrders) {
    return null
  }

  const [actionsOpen, setActionsOpen] = useState(false)
  const [editCanonicalOpen, setEditCanonicalOpen] = useState(false)
  const [auditModalOpen, setAuditModalOpen] = useState(false)
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState<string | null>(null)
  const [auditPayload, setAuditPayload] = useState<any>(null)
  const actionsMenuRef = useRef<HTMLDivElement>(null)

  // Close the actions menu when clicking outside
  useEffect(() => {
    if (!actionsOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setActionsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [actionsOpen])

  const canonicalOrder = useMemo(
    () => group.orders.find((order) => order.fulfillmentRole === 'CANONICAL' || order.isCanonical) || group.orders[0],
    [group.orders]
  )

  const handleOpenAuditModal = async () => {
    setAuditModalOpen(true)
    setAuditLoading(true)
    setAuditError(null)
    setAuditPayload(null)

    try {
      const response = await fetch(`/api/order/${currentOrderId}/audit/`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const payload = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error((payload && (payload.detail || payload.error)) || 'Unable to load split order audit.')
      }

      setAuditPayload(payload)
    } catch (error: any) {
      const message = typeof error?.message === 'string' ? error.message : 'Unable to load split order audit.'
      setAuditError(message)
    } finally {
      setAuditLoading(false)
    }
  }

  const handleCanonicalSave = async (items: FulfillmentItemUpdate[]) => {
    if (!onUpdateFulfillmentItems) {
      return
    }

    await onUpdateFulfillmentItems(items)
  }

  const renderMeta = () => {
    const meta: React.ReactNode[] = []

    if (group.referenceCode) {
      meta.push(<span key="reference">Group {group.referenceCode}</span>)
    }

    if (group.policyName) {
      meta.push(
        <span key="policy">
          Policy:{' '}
          <Link to="/order/fulfillment-policies/" data-testid="split-order-policy-link">
            {group.policyName}
          </Link>
        </span>
      )
    }

    if (!group.policyName && group.policySummary) {
      meta.push(<span key="policy-summary">{group.policySummary}</span>)
    }

    if (group.primarySiteName) {
      meta.push(<span key="primary">Primary Site: {group.primarySiteName}</span>)
    }

    if (!meta.length) {
      return null
    }

    return (
      <SplitOrdersMeta>
        {meta.map((entry, index) => (
          <React.Fragment key={index}>
            {index > 0 ? <SplitOrdersMetaSeparator aria-hidden="true">•</SplitOrdersMetaSeparator> : null}
            {entry}
          </React.Fragment>
        ))}
      </SplitOrdersMeta>
    )
  }

  return (
    <SplitOrdersCardContainer>
      <SplitOrdersHeader>
        <div>
          <h3>Split Orders</h3>
          {renderMeta()}
        </div>
        <SplitOrdersActions ref={actionsMenuRef}>
          <SplitOrdersActionsButton
            type="button"
            onClick={handleOpenAuditModal}
            aria-label="View split order JSON audit"
          >
            <FontAwesomeIcon icon={faCode} />
            JSON
          </SplitOrdersActionsButton>
          <SplitOrdersActionsButton
            type="button"
            onClick={() => setActionsOpen((open) => !open)}
            aria-expanded={actionsOpen}
            aria-label="Edit split order"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
            Edit
          </SplitOrdersActionsButton>
          {actionsOpen ? (
            <SplitOrdersActionsMenu>
              <li>
                <Link to="/order/fulfillment-policies/" onClick={() => setActionsOpen(false)}>
                  <FontAwesomeIcon icon={faSlidersH} /> Manage Fulfillment Policies
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setActionsOpen(false)
                    setEditCanonicalOpen(true)
                  }}
                >
                  <FontAwesomeIcon icon={faPenToSquare} /> Edit Canonical Order Items
                </button>
              </li>
            </SplitOrdersActionsMenu>
          ) : null}
        </SplitOrdersActions>
      </SplitOrdersHeader>
      {group.orders.map((order) => {
        const orderId = order.orderId ?? order.id
        const roleLabel = formatRole(order.fulfillmentRole || (order.isCanonical ? 'CANONICAL' : undefined))
        const statusLabel = resolveStatusLabel(order.statusLabel, order.statusId)
        const statusTone = resolveStatusTone(order.statusId)
        const warehouseLabel = order.warehouseName
        const isCurrentOrder = String(orderId) === String(currentOrderId)

        return (
          <SplitOrderEntry key={orderId} data-current={isCurrentOrder}>
            <SplitOrderEntryHeader>
              <SplitOrderIdentity>
                <span className="label">Order</span>
                <Link to={`/order/${orderId}/`}>#{orderId}</Link>
                {isCurrentOrder ? <SplitOrderBadge $variant="highlight">Current</SplitOrderBadge> : null}
              </SplitOrderIdentity>
              <div className="badges">
                {roleLabel ? <SplitOrderBadge>{roleLabel}</SplitOrderBadge> : null}
                {warehouseLabel ? <SplitOrderBadge $variant="subtle">{warehouseLabel}</SplitOrderBadge> : null}
                {statusLabel ? (
                  <SplitOrderBadge $variant="status" $statusTone={statusTone}>
                    {statusLabel}
                  </SplitOrderBadge>
                ) : null}
              </div>
            </SplitOrderEntryHeader>
            {order.items?.length ? (
              <SplitOrderItemsTable>
                <thead>
                  <tr>
                    <th scope="col">Item</th>
                    <th scope="col">SKU</th>
                    <th scope="col">Quantity</th>
                    <th scope="col">Disposition</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => {
                    const disposition = formatDisposition(item.disposition as SplitOrderItemDisposition | undefined)
                    const quantity = renderItemQuantity(item)

                    return (
                      <tr key={`${orderId}-${item.id}`}>
                        <td data-title="Item">{item.name || item.description || 'Unnamed Variant'}</td>
                        <td data-title="SKU">{item.sku || '—'}</td>
                        <td data-title="Quantity">{quantity}</td>
                        <td data-title="Disposition">
                          {disposition ? (
                            <SplitOrderBadge
                              $variant="disposition"
                              $disposition={item.disposition as SplitOrderItemDisposition | undefined}
                            >
                              {disposition}
                            </SplitOrderBadge>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </SplitOrderItemsTable>
            ) : (
              <SplitOrderEmpty>No items assigned to this order yet.</SplitOrderEmpty>
            )}
          </SplitOrderEntry>
        )
      })}
      <SplitOrdersEditCanonicalModal
        show={editCanonicalOpen}
        onClose={() => setEditCanonicalOpen(false)}
        canonicalOrder={canonicalOrder}
        onSave={onUpdateFulfillmentItems ? handleCanonicalSave : undefined}
      />
      <GenericModal heading="Split Order JSON" show={auditModalOpen} onHide={() => setAuditModalOpen(false)} size="lg">
        {auditLoading ? <p style={{ margin: 0 }}>Loading audit details…</p> : null}
        {auditError ? (
          <p style={{ color: '#f64e60', margin: '0 0 0.75rem' }} role="alert">
            {auditError}
          </p>
        ) : null}
        {!auditLoading && !auditError && auditPayload ? (
          <pre
            style={{
              background: '#f7f8ff',
              border: '1px solid #e4e6ef',
              borderRadius: '8px',
              padding: '1rem',
              maxHeight: '60vh',
              overflow: 'auto',
              margin: 0,
              fontSize: '0.875rem',
            }}
          >
            {JSON.stringify(auditPayload, null, 2)}
          </pre>
        ) : null}
        {!auditLoading && !auditError && !auditPayload ? (
          <p style={{ margin: 0 }}>No audit details available.</p>
        ) : null}
      </GenericModal>
    </SplitOrdersCardContainer>
  )
}

export default SplitOrdersCard
